import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, from, switchMap } from 'rxjs';
import { CookieService } from "ngx-cookie-service";
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private urlBase = "";
  private urlBaseNoApi = "";
  private readonly LOCAL_SESSION_TOKEN_DATA_KEY = 'localSessionToken';
  private readonly LOCAL_SESSION_ENC_KEY = 'enKey';
  private key!: string;
  private decryptedToken: any;

  constructor(private http: HttpClient, private cookies: CookieService) {
    this.urlBase = environment.urlNonce + "/api";
    this.urlBaseNoApi = environment.urlNonce;
  }
  login(user: any): Observable<any> {
    let url = this.urlBase + "/auth/login";
    return this.http.post(url, user);
  }

  validateToken(idToken: any): Observable<any> {
    let url = this.urlBase + "/azure/auth";
    return this.http.post(url, {idToken});
  }

  //SESSION TOKEN COOKIE
  /*
  @param name     Cookie name
  @param value    Cookie value
  @param expires  Number of days until the cookies expires or an actual `Date`
  @param path     Cookie path
  @param domain   Cookie domain
  @param secure   Secure flag
  @param sameSite OWASP samesite token `Lax`, `None`, or `Strict`. Defaults to `Lax` 
  */
  setSessionToken(sessionToken: string) {
    this.cookies.set("sessionToken", sessionToken, 365, '/', undefined, true, 'Strict');
  }

  getCsrfToken() {
    let url = this.urlBaseNoApi + "/csrf-token";
    return this.http.get(url);
  }

  getSessionToken() {
    return this.cookies.get("sessionToken");
  }

  deleteSessionToken() {
    //Eliminar la sessionToken en todo el sition web ""
    this.cookies.delete("sessionToken", '/');
  }

  sessionTokenExists(): boolean {
    let tokenSessionExists = this.cookies.check('sessionToken');
    return tokenSessionExists;
  }

  //SESSION TOKEN LOCAL STORAGE

  /*setEnKey(){
    const key = CryptoJS.lib.WordArray.random(32).toString();
    localStorage.setItem(this.LOCAL_SESSION_ENC_KEY, key);
  }

  getEnKey() {
    return localStorage.getItem(this.LOCAL_SESSION_ENC_KEY) || "";
  }

  hasEnKey(): boolean {
    return !!localStorage.getItem(this.LOCAL_SESSION_ENC_KEY);
  }

  removeEnKey(){
    localStorage.removeItem(this.LOCAL_SESSION_ENC_KEY);
  }*/

  // FUNCIONES DE ENCRIPTADO

  /*private encrypt(txt: string): string {
    //this.setEnKey();
    return CryptoJS.AES.encrypt(txt, environment.enKey).toString();
  }
  private decrypt(txtToDecrypt: string) {
    return CryptoJS.AES.decrypt(txtToDecrypt, environment.enKey).toString(CryptoJS.enc.Utf8);
  }*/

  async setLocalSessionToken(token: string): Promise<void> {
    const encryptedToken = await this.encryptToken(token, environment.enKey);
    localStorage.setItem(this.LOCAL_SESSION_TOKEN_DATA_KEY, encryptedToken);
  }

  async getLocalSessionToken(): Promise<string> {
    const data = localStorage.getItem(this.LOCAL_SESSION_TOKEN_DATA_KEY) || '';
    try {
      const decryptedToken = await this.decryptToken(data, environment.enKey);
      return decryptedToken;
    } catch (error) {
      console.error("Error decrypting token:", error);
      return ''; // O manejar el error de alguna manera
    }
  }

  hasLocalSessionToken(): boolean {
    return !!localStorage.getItem(this.LOCAL_SESSION_TOKEN_DATA_KEY);
  }

  removeLocalSessionToken() {
    //this.removeEnKey();
    localStorage.removeItem(this.LOCAL_SESSION_TOKEN_DATA_KEY);
  }

  async encryptToken(token: string, key: string): Promise<string> {
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(key),
      'AES-GCM',
      true,
      ['encrypt']
    );

    const encodedToken = new TextEncoder().encode(token);
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(16), // Debes proporcionar un IV (vector de inicialización) válido
      },
      cryptoKey,
      encodedToken
    );

    const encryptedArray = Array.from(new Uint8Array(encryptedBuffer));
    return btoa(String.fromCharCode.apply(null, encryptedArray));
  }

  async decryptToken(encryptedToken: string, key: string): Promise<string> {
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(key),
      'AES-GCM',
      true,
      ['decrypt']
    );
    const encryptedArray = new Uint8Array(atob(encryptedToken).split('').map((char) => char.charCodeAt(0)));
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(16), // Debes proporcionar un IV válido, debe ser el mismo que se usó para cifrar
      },
      cryptoKey,
      encryptedArray
    );
    return new TextDecoder().decode(decryptedBuffer);
  }

  /*private async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const passwordBuffer = new TextEncoder().encode(password);
    const baseKey = await window.crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );
  
    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 1000, // Ajusta el número de iteraciones según sea necesario
        hash: 'SHA-256',
      },
      baseKey,
      { name: 'AES-CBC', length: 256 }, // Ajusta la longitud según sea necesario (128 o 256 bits)
      true,
      ['encrypt', 'decrypt']
    );
  }*/

  //RESTO DE FUNCIONES

  logOut() {
    let url = this.urlBase + "/auth/logout";
    let header = new HttpHeaders({
      'Authorization': `Bearer ${this.getLocalSessionToken()}`
    });
    return this.http.get(url);
  }

  getUser(): Observable<any> {
    return from(this.getLocalSessionToken()).pipe(
      switchMap((token) => {
        let url = this.urlBase + "/auth/user";
        let header = new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          //'X-XSRF-token': '10',
        });
        const options = { headers: header };
        return this.http.get(url, options);
      })
    );
  }

  getUsers(): Observable<any> {
    return from(this.getLocalSessionToken()).pipe(
      switchMap((token) => {
        let url = this.urlBase + "/users";
        let header = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        const options = { headers: header };
        return this.http.get(url, options);
      })
    );
  }

  createUser(user: any): Observable<any> {
    return from(this.getLocalSessionToken()).pipe(
      switchMap((token) => {
        let url = this.urlBase + "/user/create";
        let header = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        const options = { headers: header };
        return this.http.post(url, user, options);
      })
    );
  }

  updateUser(user: any): Observable<any> {
    return from(this.getLocalSessionToken()).pipe(
      switchMap((token) => {
        let url = this.urlBase + "/user/update";
        let header = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        const options = { headers: header };
        return this.http.patch(url, user, options);
      })
    );
  }

  //CLIENTES

  getClients(): Observable<any> {
    return from(this.getLocalSessionToken()).pipe(
      switchMap((token) => {
        let url = this.urlBase + "/clients";
        let header = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        const options = { headers: header };
        return this.http.get(url, options);
      })
    );
  }
  getClient(clienteId: number): Observable<any> {
    return from(this.getLocalSessionToken()).pipe(
      switchMap((token) => {
        let url = this.urlBase + "/client?cliente_id=" + clienteId;
        let header = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        const options = { headers: header };
        return this.http.get(url, options);
      })
    );
  }

  createClient(client: any): Observable<any> {
    return from(this.getLocalSessionToken()).pipe(
      switchMap((token) => {
        let url = this.urlBase + "/client/create";
        let header = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        const options = { headers: header };
        return this.http.post(url, client, options);
      })
    );
  }

  updateClient(client: any): Observable<any> {
    return from(this.getLocalSessionToken()).pipe(
      switchMap((token) => {
        let url = this.urlBase + "/client/update";
        let header = new HttpHeaders({
          'Authorization': `Bearer ${token}`
        });
        const options = { headers: header };
        return this.http.patch(url, client, options);
      })
    );
  }

}
